import { z } from 'zod';

const baseFlexibleReportRequestSchema = z.object({
  id: z.number(),
  campaignId: z.number(),
  name: z.string(),
  startDate: z.string(),
  endDate: z.string(),
  locationCount: z.number(),
  status: z.enum(['draft', 'processing', 'ready', 'failed']),
  createdAt: z.string(),
  user: z.object({
    firstName: z.string(),
    lastName: z.string(),
  }),
});

export const flexibleReportRequestsResponse = z.object({
  data: z.object({
    flexibleReportRequests: z.array(baseFlexibleReportRequestSchema),
    total: z.number(),
  }),
});
export type BaseFlexibleReportRequest = z.infer<
  typeof baseFlexibleReportRequestSchema
>;
export type FlexibleReportRequestsResponse = z.infer<
  typeof flexibleReportRequestsResponse
>;

export const fullFlexibleReportRequestSchema =
  baseFlexibleReportRequestSchema.extend({
    locationListId: z.number(),
    locationListSource: z.enum(['mcm', 'poi_manager']).default('mcm'),
    flexibleReport: z
      .object({
        flexibleReportDimensions: z.array(
          z.object({
            id: z.number(),
            dimensionName: z.string(),
            dimensionValue: z.string(),
            impressions: z.number(),
            uniqueUsers: z.number(),
            incrementalVisits: z.number().nullable(),
            incrementalLift: z.number().nullable(),
            incrementalLiftConfidenceLevel: z.number().nullable(),
            projectedVisits: z.number().nullable(),
            incrementalVisitsPerImpression: z.number().nullable(),
          })
        ),
      })
      .nullable(),
    flexibleReportRequestDimensionGroups: z.array(
      z.object({
        id: z.number(),
        groupName: z.string(),
        flexibleReportRequestDimensions: z.array(
          z.object({
            id: z.number(),
            dimensionName: z.string(),
            dimensionValue: z.string(),
            rawValue: z.string(),
          })
        ),
      })
    ),
    flexibleReportRequestDmas: z.array(
      z.object({
        id: z.number(),
        dmaId: z.number(),
      })
    ),
  });

export const flexibleReportRequestResponse = z.object({
  data: z.object({
    flexibleReportRequest: fullFlexibleReportRequestSchema,
  }),
});

export type FullFlexibleReportRequest = z.infer<
  typeof fullFlexibleReportRequestSchema
>;
export type FlexibleReportRequestResponse = z.infer<
  typeof flexibleReportRequestResponse
>;

export type FlexibleReportRequestUpdateParams = Partial<{
  name: string;
  startDate: string;
  endDate: string;
  locationCount: number;
  flexibleReportRequestDmas: { dmaId: number }[];
  flexibleReportRequestDimensionGroups: {
    groupName: string;
    flexibleReportRequestDimensions: {
      dimensionName: string;
      dimensionValue: string;
    }[];
  }[];
}>;

export const locationListsResponse = z.object({
  data: z.object({
    locationLists: z.array(
      z.object({
        id: z.number(),
        name: z.string(),
      })
    ),
  }),
});

export type LocationListsResponse = z.infer<typeof locationListsResponse>;

export const dmasResponse = z.object({
  data: z.object({
    dmas: z.array(
      z.object({
        id: z.number(),
        metroName: z.string(),
        state: z.string(),
        locationCount: z.number(),
      })
    ),
  }),
});

export type DmasResponse = z.infer<typeof dmasResponse>;
export type Dma = DmasResponse['data']['dmas'][number];

export const submitErrorResponse = z.object({
  error: z.enum(['user_limit_exceeded', 'client_limit_exceeded']),
  limit: z.number(),
  windowHours: z.number().nullable(),
});

export type SubmitErrorResponse = z.infer<typeof submitErrorResponse>;
